import axios, { AxiosInstance } from 'axios';

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Any successful write may have added or changed a vendor/unit/category, so drop the lookups cache.
    if (response.config.method && response.config.method.toLowerCase() !== 'get') clearLookupsCache();
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (username: string, password: string) => {
  const { data } = await api.post('/auth/login', { email: username, password });
  return data;
};

export const getMe = async () => {
  const { data } = await api.get('/auth/me');
  return data.user;
};

// Dashboard
export const getDashboard = async (params?: {
  departmentId?: number;
  departmentIds?: string;
  startDate?: string;
  endDate?: string;
}) => {
  const { data } = await api.get('/dashboard', { params });
  return data;
};

// Material Requests

/** Decimals arrive as strings over JSON; every money/qty field is read through Number(). */
export type Numeric = number | string;

/** Catalog link carried by MR lines and by the MR line behind a PO line. */
export interface ListCatalogItemRef {
  id: number;
  partNumber: string;
  description: string | null;
  descriptionAr: string | null;
}

/** Vendor as it comes back inside list rows (Prisma Vendor, so appCode/nameAr are present). */
export interface ListVendorRef {
  id: number;
  name: string;
  code?: string | null;
  appCode?: string | null;
  nameAr?: string | null;
  [key: string]: any;
}

export interface MRListLine {
  id: number;
  lineNo: number;
  partNumber: string | null;
  itemDescription: string;
  qty: Numeric;
  unitPrice: Numeric;
  lineTotal: Numeric;
  lineApprovalStatus: string;
  lineStatus: string;
  vendorSource: string | null;
  origUnitPrice: Numeric | null;
  refinedAt: string | null;
  catalogItem: ListCatalogItemRef | null;
  vendor: ListVendorRef | null;
  unit: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
  costCenter: any | null;
  poLines?: Array<{
    id: number;
    poHeaderId: number;
    poHeader: { id: number; poId: string; status: string } | null;
    qty: Numeric;
    unitPrice: Numeric;
    lineTotal: Numeric;
    actualUnitPrice: Numeric | null;
    actualLineTotal: Numeric | null;
  }>;
  [key: string]: any;
}

export interface MRListRow {
  id: number;
  mrId: string;
  department: { id: number; code: string; name: string } | null;
  requestedBy: { id: number; displayName: string } | null;
  requestDate: string;
  neededBy: string | null;
  createdAt: string;
  urgency: string;
  status: string;
  mrTotal: Numeric | null;
  actualTotal: Numeric | null;
  /** Net of cancelled (2026-09-22): requested − cancelled lines − parts cancelled on a PO as not needed. */
  netTotal?: number;
  cancelledTotal?: number;
  /** Kept share of the linked PO lines at actual price; null when no PO line has an actual yet. */
  netActualTotal?: number | null;
  firstPoId: string | null;
  lineCount: number;
  poCount: number;
  lines: MRListLine[];
  [key: string]: any;
}

/** Counts for the same department/date scope, ignoring the status filter. */
export interface MRListAttention {
  submitted: number;
  approvedNotOrdered: number;
  neededByPassed: number;
  /** MRs with a line cancellation waiting for the CEO (2026-09-15). */
  lineCancellations?: number;
}

/** Ask to cancel an approved, not-ordered MR line (requester of the MR, procurement, CEO). Returns the refreshed MR. */
export const requestMRLineCancel = async (mrId: number | string, lineId: number, reason: string) => {
  const { data } = await api.post(`/mrs/${mrId}/lines/${lineId}/cancel-request`, { reason });
  return data;
};

/** CEO / admin: approve or reject a pending request; approve with a reason and no request = direct cancel. Returns the refreshed MR. */
export const decideMRLineCancel = async (
  mrId: number | string,
  lineId: number,
  body: { decision: 'approve' | 'reject'; reason?: string },
) => {
  const { data } = await api.post(`/mrs/${mrId}/lines/${lineId}/cancel-decision`, body);
  return data;
};

export interface MRListResponse {
  data: MRListRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  statusCounts: Record<string, number>;
  attention: MRListAttention;
}

export const listMRs = async (params?: {
  departmentId?: number;
  departmentIds?: string;
  status?: string;
  statuses?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  lineCancellations?: string;
}): Promise<MRListResponse> => {
  const { data } = await api.get('/mrs', { params });
  return data;
};

export const getMR = async (mrId: number | string) => {
  const { data } = await api.get(`/mrs/${mrId}`);
  return data;
};

export const createMR = async (mrData: {
  departmentId: number;
  neededBy: string;
  urgency: string;
  notes?: string;
  lines: Array<{
    partNumber?: string;
    itemDescription: string;
    categoryId?: number;
    vendorId?: number;
    vendorName?: string;
    qty: number;
    unitId?: number;
    unitPrice: number;
    currencyId?: number;
    lineNotes?: string;
  }>;
}) => {
  const { data } = await api.post('/mrs', mrData);
  return data;
};

export const updateMR = async (mrId: string | number, mrData: Record<string, any>) => {
  const { data } = await api.put(`/mrs/${mrId}`, mrData);
  return data;
};

export const submitMR = async (mrId: number) => {
  const { data } = await api.post(`/mrs/${mrId}/submit`);
  return data;
};

/** Pull a Submitted MR back to Draft (requester only, while no line has been acted on). */
export const recallMR = async (mrId: number) => {
  const { data } = await api.post(`/mrs/${mrId}/recall`);
  return data;
};

export const approveLines = async (
  mrId: number,
  approvals: Array<{
    lineId: number;
    approved: boolean;
    rejectReason?: string;
  }>
) => {
  const { data } = await api.post(`/mrs/${mrId}/approve`, { approvals });
  return data;
};

export const getApprovableLines = async (departmentId?: number) => {
  const { data } = await api.get('/mrs/approvable', {
    params: departmentId ? { departmentId } : undefined,
  });
  return data;
};

// Purchase Orders

export interface POListLine {
  id: number;
  lineNo: number;
  partNumber: string | null;
  itemDescription: string;
  qty: Numeric;
  unitPrice: Numeric;
  lineTotal: Numeric;
  actualUnitPrice: Numeric | null;
  actualLineTotal: Numeric | null;
  totalReceived: Numeric;
  balanceQty: Numeric;
  unit: { name: string } | null;
  currency: { code: string } | null;
  costCenter: any | null;
  mrLine: {
    lineNo: number;
    catalogItem: ListCatalogItemRef | null;
    mrHeader: { mrId: string; department: { id: number; code: string; name: string } | null } | null;
  } | null;
  [key: string]: any;
}

export interface POListRow {
  id: number;
  poId: string;
  department: { id: number; code: string; name: string } | null;
  vendor: ListVendorRef | null;
  createdBy: { id: number; displayName: string } | null;
  poDate: string;
  createdAt: string;
  status: string;
  poTotal: Numeric | null;
  actualTotal: Numeric | null;
  receivedValue: Numeric;
  openValue: Numeric;
  /** Net of cancelled (2026-09-22): shown total − cancelled part; stored totals stay as ordered. */
  netValue?: number;
  cancelledValue?: number;
  netEstimate?: number;
  cancelledEstimate?: number;
  lineCount: number;
  firstMrId: string | null;
  lateFlag: boolean;
  overdueFlag: boolean;
  poAgeDays: number | null;
  lines: POListLine[];
  [key: string]: any;
}

/** Counts for the same department/date scope, ignoring the status filter. */
export interface POListAttention {
  overEstimate: number;
  late: number;
  openValue: number;
  /** POs with a line cancellation waiting for approval (2026-09-14). */
  lineCancellations?: number;
}

export interface POListResponse {
  data: POListRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  statusCounts: Record<string, number>;
  attention: POListAttention;
}

export const listPOs = async (params?: {
  departmentId?: number;
  departmentIds?: string;
  status?: string;
  statuses?: string;
  vendorId?: number;
  vendorIds?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<POListResponse> => {
  const { data } = await api.get('/pos', { params });
  return data;
};

export const getPO = async (poId: number | string) => {
  const { data } = await api.get(`/pos/${poId}`);
  return data;
};

export const createPO = async (mrLineIds: number[], notes?: string, reviewed?: boolean) => {
  const { data } = await api.post('/pos', { mrLineIds, notes, reviewed });
  return data;
};

export const updatePOLines = async (
  poId: string,
  updates: Array<{ poLineId: number; partNumber?: string; actualUnitPrice?: number }>
) => {
  const { data } = await api.put(`/pos/${poId}/lines`, { updates });
  return data;
};

export const updatePOStatus = async (poId: number | string, status: string, reason?: string, mrAction?: 'reorder' | 'not_needed') => {
  const { data } = await api.put(`/pos/${poId}/status`, { status, reason, mrAction });
  return data;
};

export const receivePO = async (
  poId: number | string,
  items: Array<{ poLineId: number; receivedQty: number; notes?: string }>
) => {
  const { data } = await api.post(`/pos/${poId}/receive`, { items });
  return data;
};

// Lookups — cached per signed-in token for 5 minutes (2026-09-25): 31 screens called this on every mount,
// firing 5 requests each time. Cleared by any successful write (see the response interceptor).
const LOOKUPS_TTL_MS = 5 * 60 * 1000;
let lookupsCache: { token: string | null; at: number; promise: ReturnType<typeof fetchLookups> } | null = null;

export function clearLookupsCache() {
  lookupsCache = null;
}

export const getLookups = async () => {
  const token = localStorage.getItem('token');
  if (!lookupsCache || lookupsCache.token !== token || Date.now() - lookupsCache.at > LOOKUPS_TTL_MS) {
    const entry = { token, at: Date.now(), promise: fetchLookups() };
    lookupsCache = entry;
    entry.promise.catch(() => { if (lookupsCache === entry) lookupsCache = null; });
  }
  const l = await lookupsCache.promise;
  // Fresh arrays per caller so a screen that edits its copy cannot change what the next screen sees.
  return {
    departments: [...(l.departments || [])],
    vendors: [...(l.vendors || [])],
    categories: [...l.categories],
    categoryTree: [...l.categoryTree],
    units: [...(l.units || [])],
    currencies: [...(l.currencies || [])],
  };
};

const fetchLookups = async () => {
  const [departments, vendors, categories, units, currencies] =
    await Promise.all([
      api.get('/lookups/departments'),
      api.get('/lookups/vendors'),
      api.get('/lookups/categories'),
      api.get('/lookups/units'),
      api.get('/lookups/currencies'),
    ]);
  return {
    departments: departments.data,
    vendors: vendors.data,
    categories: Array.isArray(categories.data) ? categories.data : (categories.data?.flat || []),
    categoryTree: Array.isArray(categories.data) ? [] : (categories.data?.tree || []),
    units: units.data,
    currencies: currencies.data,
  };
};

// Admin - CRUD for lookups
export const createVendor = async (vendorData: { name: string; code?: string; contactInfo?: string }) => {
  const { data } = await api.post('/lookups/vendors', vendorData);
  return data;
};

export const createCategory = async (categoryData: { name: string; code?: string; parentId?: number; description?: string }) => {
  const { data } = await api.post('/lookups/categories', categoryData);
  return data;
};

export const createUnit = async (unitData: { name: string; abbreviation?: string }) => {
  const { data } = await api.post('/lookups/units', unitData);
  return data;
};

export const listUsers = async () => {
  const { data } = await api.get('/auth/users');
  return data;
};

export const createUser = async (userData: {
  email: string;
  password: string;
  displayName: string;
  roles: string[];
  departmentId: number;
  extraDepartmentIds?: number[];
}) => {
  const { data } = await api.post('/auth/register', userData);
  return data;
};

export const updateUser = async (userId: number, data: { displayName?: string; email?: string; role?: string; roles?: string[]; departmentId?: number; extraDepartmentIds?: number[]; isActive?: boolean; password?: string }) => {
  const { data: result } = await api.put(`/auth/users/${userId}`, data);
  return result;
};

export const deleteUser = async (userId: number, force?: boolean) => {
  const { data } = await api.delete(`/auth/users/${userId}`, { params: force ? { force: 'true' } : undefined });
  return data;
};

// Permissions
export const getMyPermissions = async (): Promise<Record<string, string>> => {
  const { data } = await api.get('/permissions/my');
  return data;
};

export const listAllPermissions = async (filters?: { grantType?: string; grantValue?: string }) => {
  const { data } = await api.get('/permissions', { params: filters });
  return data;
};

export const listResources = async () => {
  const { data } = await api.get('/permissions/resources');
  return data;
};

export const upsertPermission = async (perm: { resource: string; accessLevel: string; grantType: string; grantValue: string }) => {
  const { data } = await api.put('/permissions', perm);
  return data;
};

export const deletePermission = async (id: number) => {
  const { data } = await api.delete(`/permissions/${id}`);
  return data;
};

export const amendMRLine = async (mrId: number | string, lineId: number, updates: { qty?: number; rejectReason?: string; lineApprovalStatus?: string }) => {
  const { data } = await api.put(`/mrs/${mrId}/lines/${lineId}`, updates);
  return data;
};

export const updateVendor = async (vendorId: number, data: { name?: string; code?: string; contactInfo?: string }) => {
  const { data: result } = await api.put(`/lookups/vendors/${vendorId}`, data);
  return result;
};

export const deleteVendor = async (vendorId: number) => {
  const { data } = await api.delete(`/lookups/vendors/${vendorId}`);
  return data;
};

export const updateCategory = async (categoryId: number, data: { name?: string; code?: string; parentId?: number; description?: string; isActive?: boolean }) => {
  const { data: result } = await api.put(`/lookups/categories/${categoryId}`, data);
  return result;
};

export const deleteCategory = async (categoryId: number) => {
  const { data } = await api.delete(`/lookups/categories/${categoryId}`);
  return data;
};

export const updateUnit = async (unitId: number, data: { name?: string; abbreviation?: string }) => {
  const { data: result } = await api.put(`/lookups/units/${unitId}`, data);
  return result;
};

export const deleteUnit = async (unitId: number) => {
  const { data } = await api.delete(`/lookups/units/${unitId}`);
  return data;
};

export const deleteMR = async (mrId: number | string) => {
  const { data } = await api.delete(`/mrs/${mrId}`);
  return data;
};

export const deleteMRLine = async (mrId: number | string, lineId: number) => {
  const { data } = await api.delete(`/mrs/${mrId}/lines/${lineId}`);
  return data;
};

export type POLineMrAction = 'reorder' | 'not_needed';

// Cancellations queue (2026-09-16): one read model over whole-PO, PO-line and MR-line requests.
export interface CancellationRow {
  key: string;
  type: 'po' | 'po_line' | 'mr_line';
  status: 'Pending' | 'Approved' | 'Rejected';
  document: string;
  documentId: number;
  documentPath: string;
  lineId?: number;
  lineNo?: number;
  item: { code: string | null; description: string; descriptionAr: string | null };
  qty: number;
  ofQty: number;
  unit: string | null;
  received: number;
  value: number;
  department: { id: number; code: string; name: string } | null;
  vendor: { id: number; name: string } | null;
  requestedBy: string | null;
  requestedById: number | null;
  requestedAt: string | null;
  ageHours: number | null;
  reason: string | null;
  mrAction: POLineMrAction | null;
  mrRef: string | null;
  decidedBy?: string | null;
  decidedAt?: string | null;
  children?: CancellationRow[];
}
export interface CancellationQueueResult {
  status: 'pending' | 'decided';
  summary: { count: number; lines: number; value: number; oldestHours: number; byType: { po: number; po_line: number; mr_line: number } };
  rows: CancellationRow[];
  truncated: boolean;
  canDecide: boolean;
}
export const listCancellations = async (params?: { status?: 'pending' | 'decided'; type?: CancellationRow['type']; limit?: number; days?: number; departmentIds?: string }) => {
  const { data } = await api.get('/cancellations', { params });
  return data as CancellationQueueResult;
};

/** Ask to cancel part or all of a PO line's open balance; the CEO decides (2026-09-14). Returns the refreshed PO. */
export const requestPOLineCancel = async (
  poId: number | string,
  lineId: number,
  body: { qty: number; reason: string; mrAction: POLineMrAction },
) => {
  const { data } = await api.post(`/pos/${poId}/lines/${lineId}/cancel-request`, body);
  return data;
};

/** CEO / admin decision on a pending line cancellation. Returns the refreshed PO. */
export const decidePOLineCancel = async (
  poId: number | string,
  lineId: number,
  body: { decision: 'approve' | 'reject'; mrAction?: POLineMrAction },
) => {
  const { data } = await api.post(`/pos/${poId}/lines/${lineId}/cancel-decision`, body);
  return data;
};

export const deletePOLine = async (poId: number | string, lineId: number) => {
  const { data } = await api.delete(`/pos/${poId}/lines/${lineId}`);
  return data;
};

export const bulkUpdatePOStatus = async (
  poIds: number[],
  status: string,
  reason?: string
) => {
  const { data } = await api.put('/pos/bulk-status', { poIds, status, reason });
  return data;
};

export const deletePO = async (poId: number | string) => {
  const { data } = await api.delete(`/pos/${poId}`);
  return data;
};

// Goods Receiving Notes (GRN)
export const listGRNs = async (params?: {
  poId?: string;
  vendorId?: number;
  search?: string;
  vendorIds?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) => {
  const { data } = await api.get('/grns', { params });
  return data;
};

export const getGRN = async (grnId: string) => {
  const { data } = await api.get(`/grns/${grnId}`);
  return data;
};

export const createGRN = async (payload: {
  poId: string | number;
  deliveryNoteRef?: string;
  supplierInvoice?: string;
  receivedOn?: string;
  notes?: string;
  lines: Array<{
    poLineId: number;
    deliveredQty: number;
    acceptedQty: number;
    rejectedQty?: number;
    inspectionNotes?: string;
    rejectReason?: string;
    notes?: string;
  }>;
}) => {
  const { data } = await api.post('/grns', payload);
  return data;
};

// ── GRN phase 1 (2026-10-21): two purchase paths and the new receive window (/api/receiving) ──────────
export type PaymentMethod = 'cash' | 'card' | 'transfer' | 'on_account';
export type RejectOutcome = 'return' | 'replacement' | 'discount' | 'writeoff';
export interface ProofFile { key: string; name: string; size: number; mime: string }
export interface LeadTime { vendorId: number; vendor: string; days: number; basis: 'history' | 'vendor' | 'default'; orders: number }
export interface ReceiveLinePayload {
  poLineId: number;
  arrivedQty: number;
  rejectedQty?: number;
  rejectReason?: string;
  actualUnitPrice?: number;
  costCenterId?: number | null;
  outcome?: { type: RejectOutcome; dueDate?: string; amount?: number; note?: string };
  rest?: { action: 'coming' | 'close'; date?: string; reason?: string; mrAction?: 'reorder' | 'not_needed' };
}
export interface ReceivePayload {
  receivedOn: string;
  receivedOnReason?: string;
  deliveryNoteRef?: string;
  supplierInvoice?: string;
  proofFiles?: ProofFile[];
  notes?: string;
  lines: ReceiveLinePayload[];
}
export interface DirectPurchasePayload {
  boughtOn: string;
  boughtOnReason?: string;
  paymentMethod: PaymentMethod;
  proofFiles?: ProofFile[];
  noProofReason?: string;
  notes?: string;
  lines: Array<{ mrLineId: number; qty: number; unitPrice: number; costCenterId?: number | null }>;
}
export interface ExpectedDeliveryRow {
  id: number; poId: string; status: string;
  vendor: { id: number; name: string }; mrIds: string[];
  expected: string | null; lateDays: number; quietDays: number | null; partly: boolean; replacement: boolean;
  openValue: number;
  openLines: Array<{ id: number; lineNo: number; item: string; code: string | null; open: number; qty: number; unit: string | null; pendingCancel: boolean }>;
  state: 'stale' | 'late' | 'due_today' | 'this_week' | 'later';
}
export interface ExpectedDeliveries {
  today: string;
  counts: { dueToday: number; late: number; lateOldestDays: number; thisWeek: number; stale: number; staleValue: number; replacements: number };
  rows: ExpectedDeliveryRow[];
}
export const uploadReceiptProof = async (files: File[]) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return (await api.post('/receiving/proof', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data as ProofFile[];
};
export const getReceiptProofLink = async (grnId: string, index: number) =>
  (await api.get(`/receiving/proof/${grnId}/${index}`)).data.url as string;
export const getVendorLeadTime = async (vendorId: number) => (await api.get(`/receiving/lead-time/${vendorId}`)).data as LeadTime;
export const createVendorOrder = async (mrLineIds: number[], expectedDelivery: string, notes?: string) =>
  (await api.post('/receiving/order', { mrLineIds, expectedDelivery, notes })).data as any[];
export const createDirectPurchase = async (payload: DirectPurchasePayload) =>
  (await api.post('/receiving/direct', payload)).data as { po: any; grn: any; warnings: string[] };
export const receiveGoods = async (poId: string, payload: ReceivePayload) =>
  (await api.post(`/receiving/${poId}/receive`, payload)).data as { grn: any; warnings: string[] };
export const getExpectedDeliveries = async () => (await api.get('/receiving/expected')).data as ExpectedDeliveries;

export const createGRNReturn = async (grnLineId: number, payload: {
  returnQty: number;
  returnReason: string;
  notes?: string;
}) => {
  const { data } = await api.post(`/grns/lines/${grnLineId}/returns`, payload);
  return data;
};

export const updateGRNReturnStatus = async (returnId: number, payload: Record<string, any>) => {
  const { data } = await api.patch(`/grns/returns/${returnId}`, payload);
  return data;
};

export const deleteGRN = async (grnId: string) => {
  const { data } = await api.delete(`/grns/${grnId}`);
  return data;
};

// Parts Catalog
export const catalogList = async (params?: Record<string, any>) => {
  const { data } = await api.get('/catalog', { params });
  return data as { items: CatalogItem[]; total: number; page: number; pageSize: number };
};

export const catalogGet = async (id: number) => {
  const { data } = await api.get(`/catalog/${id}`);
  return data as CatalogItemDetail;
};

export const catalogCreate = async (payload: CatalogCreatePayload) => {
  const { data } = await api.post('/catalog', payload);
  return data as CatalogItem;
};

export const catalogUpdate = async (id: number, payload: CatalogUpdatePayload) => {
  const { data } = await api.patch(`/catalog/${id}`, payload);
  return data as CatalogItem;
};

export const catalogDelete = async (id: number) => {
  const { data } = await api.delete(`/catalog/${id}`);
  return data as CatalogItem;
};

export const catalogSearch = async (q: string, limit?: number) => {
  const { data } = await api.get('/catalog/search', { params: { q, limit } });
  return data as CatalogSearchResult[];
};

export const catalogBulkCreate = async (items: Array<{
  partNumber: string;
  description: string;
  defaultUnitPrice?: number;
  unitName?: string;
  categoryName?: string;
  vendorName?: string;
  currencyCode?: string;
  sku?: string;
  barcode?: string;
}>) => {
  const { data } = await api.post('/catalog/bulk', { items });
  return data as { created: CatalogItem[]; errors: Array<{ row: number; partNumber: string; error: string }>; totalProcessed: number };
};

// Catalog browser (MR create modal): paged, normalised search (Arabic letter shapes, digits, hyphens ignored).
export interface CatalogBrowseResult {
  items: CatalogSearchResult[];
  total: number;
  page: number;
  pageSize: number;
}
export const catalogBrowse = async (params: { q?: string; class?: string; page?: number; pageSize?: number }) => {
  const { data } = await api.get('/catalog/browse', { params });
  return data as CatalogBrowseResult;
};

// Admin: department settings (requesters may enter an estimated unit price)
export const updateDepartmentSettings = async (id: number, payload: { requesterEntersPrice?: boolean }) => {
  const { data } = await api.patch(`/lookups/departments/${id}`, payload);
  return data;
};

// Price engine (2026-09-06)
export interface ItemVendorPrice { vendorId: number; vendorName: string; appCode: string | null; preferred: boolean; listPrice: number | null; listDate: string | null; currency: string | null; leadTimeDays: number | null; vendorPartNo: string | null; vendorColour: string | null; lastPaid: number | null; lastPo: string | null; lastPoDate: string | null; driftPct: number | null; current: number | null }
export interface ItemPrices { itemId: number; partNumber: string; unit: { id: number; name: string } | null; current: { price: number | null; vendorId: number | null; vendorName: string | null; rule: string; date: string | null }; vendors: ItemVendorPrice[]; timeline: Array<{ date: string; source: string; price: number; vendor: string; ref: string | null; by: string | null }>; altUnits: Array<{ id: number; name: string; factor: number }>; deptPreferences: DeptPreference[] }
export interface DeptPreference { departmentId: number; departmentCode: string; departmentName: string; vendorId: number; vendorName: string; appCode: string | null; reason: string | null; setBy: string | null; setAt: string }
export const catalogDeptVendors = (id: number) => api.get(`/catalog/${id}/dept-vendors`).then((r) => r.data as DeptPreference[]);
export const setCatalogDeptVendor = (id: number, departmentId: number, payload: { vendorId: number; reason?: string }) => api.put(`/catalog/${id}/dept-vendors/${departmentId}`, payload).then((r) => r.data as DeptPreference[]);
export const removeCatalogDeptVendor = (id: number, departmentId: number) => api.delete(`/catalog/${id}/dept-vendors/${departmentId}`).then((r) => r.data as DeptPreference[]);
export const catalogItemPrices = (id: number) => api.get(`/catalog/${id}/prices`).then((r) => r.data as ItemPrices);
export const catalogPricesBatch = (ids: number[]) => api.get('/catalog/prices', { params: { ids: ids.join(',') } }).then((r) => r.data as Record<number, ItemPrices>);

// Item Card (2026-09-09): everything connected to one catalog item in one call.
export interface CatalogCardRequest { mrLineId: number; mrHeaderId: number; mrId: string; lineNo: number; departmentId: number | null; department: string | null; requester: string | null; requestDate: string | null; neededBy: string | null; urgency: string | null; mrStatus: string | null; qty: number | null; unit: string | null; unitPrice: number | null; currency: string | null; lineStatus: string | null; approvalStatus: string | null; vendorId: number | null; vendor: string | null; vendorSource: string | null; costCenterCode: string | null; costCenterName: string | null; lineNotes: string | null; poLineId: number | null; poHeaderId: number | null; poId: string | null }
export interface CatalogCardOrder { poLineId: number; poHeaderId: number; poId: string; poDate: string | null; status: string | null; vendorId: number | null; vendor: string | null; appCode: string | null; qty: number | null; unitPrice: number | null; actualUnitPrice: number | null; pricePaid: number | null; lineTotal: number | null; actualLineTotal: number | null; currency: string | null; totalReceived: number; returnedQty: number; mrHeaderId: number; mrId: string; department: string | null; createdBy: string | null; costCenterCode: string | null; open: boolean }
export interface CatalogCardReturn { id: number; qty: number | null; date: string | null; reason: string | null; status: string | null; creditNoteRef: string | null; refundAmount: number | null; returnedBy: string | null }
export interface CatalogCardReceipt { grnLineId: number; grnHeaderId: number; grnId: string; receivedOn: string | null; poHeaderId: number; poId: string; vendor: string | null; deliveredQty: number; acceptedQty: number; rejectedQty: number; inspectionStatus: string | null; rejectReason: string | null; receivedBy: string | null; deliveryNoteRef: string | null; supplierInvoice: string | null; returns: CatalogCardReturn[] }
export interface CatalogCardVendor { vendorId: number; name: string; appCode: string | null; preferred: boolean; poCount: number; orderedQty: number; receivedQty: number; returnedQty: number; acceptedQty: number; rejectedQty: number; grnPassed: number; grnFailed: number; spendAccepted: number | null; avgPrice: number | null; firstPo: string | null; firstPoId: string; lastPo: string | null; lastPoId: string; openLines: number }
export interface CatalogCardCostCenter { code: string | null; name: string | null; lines: number; qty: number; mrCount: number; value: number | null }
export interface CatalogCard {
  item: { id: number; partNumber: string; description: string; descriptionAr: string | null; sku: string | null; barcode: string | null; isActive: boolean; defaultUnitPrice: number | null; currency: string | null; unitId: number | null; unit: string | null; unitSet: boolean; unitFromLines: string | null; categoryId: number | null; category: string | null; preferredVendor: { id: number; name: string; appCode: string | null } | null; leadTimeDays: number | null; minStockLevel: number | null; maxStockLevel: number | null; reorderPoint: number | null; allowedUnitCategories: string | null; createdAt: string | null; createdBy: string | null; updatedAt: string | null; updatedBy: string | null };
  scope: { restricted: boolean; showMoney: boolean; departmentIds: number[] };
  stats: { requestLines: number; rejectedLines: number; requestedQty: number; approvedQty: number; departments: string[]; poCount: number; poLines: number; openPoCount: number; outstandingQty: number; vendorCount: number; orderedQty: number; receivedQty: number; acceptedQty: number; rejectedQty: number; returnedQty: number; grnCount: number; failedInspections: number; spendAccepted: number | null; orderedValue: number | null; avgPricePaid: number | null; lastPurchase: { poId: string; poHeaderId: number; poDate: string | null; qty: number | null; vendor: string | null; unitPrice: number | null } | null; firstPurchaseDate: string | null; currency: string };
  requests: CatalogCardRequest[]; orders: CatalogCardOrder[]; receipts: CatalogCardReceipt[]; vendors: CatalogCardVendor[]; costCenters: CatalogCardCostCenter[];
  audit: Array<{ at: string | null; who: string | null; what: string; detail: string | null; kind: 'record' | 'price' }>;
}
export const catalogCard = (id: number) => api.get(`/catalog/${id}/card`).then((r) => r.data as CatalogCard);

// Composer ↔ ERP sync (2026-09-09): pushes from the Item Composer desktop app are staged for review here.
export interface ComposerSyncBucketRow { code: string; cls: string | null; type: string | null; descEn: string; descAr: string; department: string | null; note: string | null; bucket: 'added' | 'changed' | 'removed' | 'dupSkipped' | 'shadeConflict'; changes?: string[]; current?: { id: number; description: string; descriptionAr: string | null; isActive: boolean } | null; conflictWith?: string | null; reactivation?: boolean; vendorRefs?: number; /** sha256s, present only when the picture differs (2026-09-22) */ picture?: { from: string | null; to: string | null } }
export interface ComposerSyncSummary { added: number; changed: number; removed: number; dupSkipped: number; shadeConflict: number; pictures?: number; vocab?: { dictValues: number; dictTypes: number; dictRetire: number; shades: number; motifs: number; departments: number; uoms: number } }
export interface ComposerSyncBatch { id: number; direction: 'push' | 'pull'; status: string; enginePin: string | null; dictHash: string | null; fullSnapshot: boolean; itemCount: number; summary: ComposerSyncSummary | null; sourceIp: string | null; receivedAt: string; reviewedBy: string | null; reviewedAt: string | null; report: any | null }
export interface ComposerSyncBatchDetail extends ComposerSyncBatch { rows: ComposerSyncBucketRow[] }
export const composerSyncBatches = () => api.get('/items/sync/batches').then((r) => r.data as ComposerSyncBatch[]);
export const composerSyncBatch = (id: number) => api.get(`/items/sync/batches/${id}`).then((r) => r.data as ComposerSyncBatchDetail);
export const composerSyncApply = (id: number, accept: { acceptAll?: boolean; acceptCodes?: string[]; removeCodes?: string[] }) => api.post(`/items/sync/batches/${id}/apply`, accept).then((r) => r.data as { report: any; batch: ComposerSyncBatch });
export const composerSyncReject = (id: number) => api.post(`/items/sync/batches/${id}/reject`).then((r) => r.data as ComposerSyncBatch);
export const composerSyncStatus = () => api.get('/items/sync/status').then((r) => r.data as { tokenConfigured: boolean; lastPush: string | null; lastPull: string | null; staged: number; itemsWithComposerData: number; enginePin: string });

// Item pictures (2026-09-22): set in the composer, stored in Spaces, read here through short-lived
// presigned URLs. The ERP never uploads or edits one.
export interface CatalogPicture {
  sha256: string;
  mime: string;
  size: number | null;
  /** Presigned GET, valid for 15 minutes. */
  url: string;
  /** Optional: when the composer last set it, if the server reports it. */
  updatedAt?: string | null;
}
/** Only items that currently have a picture come back. Max 1000 ids per call. */
export const catalogPictures = (ids: number[]) =>
  api.get('/catalog/pictures', { params: { ids: ids.join(',') } }).then((r) => r.data as Record<number, CatalogPicture>);
/** Review thumbnails: the server proxies the bytes for either side of the change (`to` = pushed, `from` = what it replaces). */
export const composerSyncStagedPictureUrl = async (batchId: number, code: string, which: 'to' | 'from' = 'to'): Promise<string> => {
  const { data } = await api.get(`/items/sync/batches/${batchId}/pictures/${encodeURIComponent(code)}`, { params: { which }, responseType: 'blob' });
  return URL.createObjectURL(data as Blob);
};

// Master Items sheet import (xlsx from the part-number app). Dry run unless commit.
export interface CatalogMasterImportResult {
  dryRun: boolean;
  fileName: string | null;
  totalRows: number;
  added: Array<{ code: string; description: string }>;
  updated: Array<{ code: string; changes: string[] }>;
  unchanged: number;
  skipped: Array<{ row: number; code: string; reason: string }>;
  warnings: Array<{ row: number; code: string; message: string }>;
  unmappedUnits: Record<string, number>;
  unmappedVendors: Record<string, number>;
  classCategoryMap: Record<string, string | null>;
  vendorPrices?: { rows: number; added: number; updated: number; defaultsChanged: number; vendorsCreated: string[]; unmappedVendorCodes: Record<string, number> };
  altUnits?: { rows: number; applied: number; unmappedUnits: string[] };
}
export const catalogImportMaster = async (file: File, commit: boolean) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post(`/catalog/import/master${commit ? '?commit=1' : ''}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data as CatalogMasterImportResult;
};

// Catalog shared types (exported for use in components)
export const refineMRLine = async (lineId: number, data: Record<string, any>) => {
  const { data: result } = await api.patch(`/mrs/lines/${lineId}/refine`, data);
  return result;
};

export interface CatalogItem {
  id: number;
  partNumber: string;
  description: string;
  descriptionAr?: string | null;
  defaultUnitPrice: number | null;
  sku: string | null;
  barcode: string | null;
  active: boolean;
  isActive?: boolean; // server field name; `active` kept for older callers
  allowedUnitCategories?: string | null;
  unit: { id: number; name: string } | null;
  category: { id: number; name: string } | null;
  preferredVendor: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
  // Inventory Phase 1.6: structured part-number segments
  classCode?: string | null;
  itemCode?: string | null;
  colorCode?: string | null;
  colorVariant?: string | null;
  sizeCode?: string | null;
  softnessCode?: string | null;
  supplierCode?: string | null;
}

export interface CatalogPriceHistory {
  id: number;
  changedAt: string;
  oldPrice: number | null;
  newPrice: number | null;
  changedBy: { id: number; displayName: string } | null;
  reason: string | null;
}

export interface CatalogItemDetail extends CatalogItem {
  priceHistory: CatalogPriceHistory[];
}

export interface CatalogSearchResult {
  id: number;
  partNumber: string;
  description: string;
  descriptionAr?: string | null;
  defaultUnitPrice: number | null;
  allowedUnitCategories?: string | null;
  unit: { id: number; name: string } | null;
  category: { id: number; name: string } | null;
  preferredVendor: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
}

export interface CatalogCreatePayload {
  partNumber: string;
  description: string;
  defaultUnitPrice?: number | null;
  unitId?: number | null;
  categoryId?: number | null;
  preferredVendorId?: number | null;
  currencyId?: number | null;
  sku?: string | null;
  barcode?: string | null;
  allowedUnitCategories?: string | null;
  // Inventory Phase 1.6: structured part-number segments
  classCode?: string | null;
  itemCode?: string | null;
  colorCode?: string | null;
  colorVariant?: string | null;
  sizeCode?: string | null;
  softnessCode?: string | null;
  supplierCode?: string | null;
}

export interface CatalogUpdatePayload extends Partial<CatalogCreatePayload> {
  priceChangeReason?: string;
}

// =====================================================================
// Audit Trail
// =====================================================================

export const getAuditTrail = (params?: Record<string, any>) =>
  api.get('/audit', { params }).then(r => r.data);

export const getEntityHistory = (entityType: string, entityId: string) =>
  api.get(`/audit/entity/${entityType}/${entityId}`).then(r => r.data);

export const getAuditEntityTypes = () =>
  api.get('/audit/entity-types').then(r => r.data);

// =====================================================================
// Vendor Management
// =====================================================================

export const vendorList = (params?: Record<string, any>) =>
  api.get('/vendors', { params }).then(r => r.data);

export interface VendorOverviewRow { id: number; appCode: string | null; nameAr: string | null; supplies: string[]; pricedItems: number; lastPoId: string | null; lastPoDate: string | null; spend12m: number; onTimePct: number | null; onTimeN: number; driftPct: number | null }
export const vendorOverview = () => api.get('/vendors/overview').then((r) => r.data as VendorOverviewRow[]);
export const vendorStatsExtended = () => api.get('/vendors/stats-extended').then((r) => r.data);
export const vendorDuplicates = () => api.get('/vendors/duplicates').then((r) => r.data as Array<{ key: string; vendors: Array<{ id: number; name: string; status: string; pos: number }> }>);
export const vendorPendingQueue = () => api.get('/vendors/pending-queue').then((r) => r.data as Array<{ id: number; name: string; createdAt: string; requestedFromMrId: string | null; mrLines: number; department: string | null; requester: string | null; matches: Array<{ id: number; name: string; appCode: string | null; status: string; score: number }> }>);
export const vendorMetrics = (id: number) => api.get(`/vendors/${id}/metrics`).then((r) => r.data);
export const vendorStats = () =>
  api.get('/vendors/stats').then(r => r.data);

export const vendorFilters = () =>
  api.get('/vendors/filters').then(r => r.data);

export const vendorGet = (id: number) =>
  api.get(`/vendors/${id}`).then(r => r.data);

export const vendorCreate = (data: Record<string, any>) =>
  api.post('/vendors', data).then(r => r.data);

export const vendorUpdate = (id: number, data: Record<string, any>) =>
  api.patch(`/vendors/${id}`, data).then(r => r.data);

export const vendorDeactivate = (id: number) =>
  api.delete(`/vendors/${id}`).then(r => r.data);

export const vendorSimilar = (name: string, excludeId?: number) =>
  api.get('/vendors/similar', { params: { name, excludeId } }).then(r => r.data);

export const vendorMergePreview = (targetId: number, sourceIds: number[]) =>
  api.post('/vendors/merge/preview', { targetId, sourceIds }).then(r => r.data);

export const vendorMerge = (targetId: number, sourceIds: number[]) =>
  api.post('/vendors/merge', { targetId, sourceIds }).then(r => r.data);

export const vendorContacts = (vendorId: number) =>
  api.get(`/vendors/${vendorId}/contacts`).then(r => r.data);

export const vendorContactCreate = (vendorId: number, data: Record<string, any>) =>
  api.post(`/vendors/${vendorId}/contacts`, data).then(r => r.data);

export const vendorContactUpdate = (vendorId: number, contactId: number, data: Record<string, any>) =>
  api.patch(`/vendors/${vendorId}/contacts/${contactId}`, data).then(r => r.data);

export const vendorContactDelete = (vendorId: number, contactId: number) =>
  api.delete(`/vendors/${vendorId}/contacts/${contactId}`).then(r => r.data);

export const vendorApprove = (id: number, updates?: Record<string, any>) =>
  api.post(`/vendors/${id}/approve`, { updates }).then(r => r.data);

export const vendorReject = (id: number, reason: string) =>
  api.post(`/vendors/${id}/reject`, { reason }).then(r => r.data);

// =====================================================================
// Purchase Returns
// =====================================================================

export const listReturns = (params?: Record<string, any>) =>
  api.get('/returns', { params }).then(r => r.data);

export const getReturnStats = () =>
  api.get('/returns/stats').then(r => r.data);

export const getReturnReasonCodes = () =>
  api.get('/returns/reason-codes').then(r => r.data);

export const updateReturn = (id: number, data: Record<string, any>) =>
  api.patch(`/returns/${id}`, data).then(r => r.data);

export const bulkSettleReturns = (data: Record<string, any>) =>
  api.post('/returns/bulk-settle', data).then(r => r.data);

export const getVendorReturns = (vendorId: number) =>
  api.get(`/returns/vendor/${vendorId}`).then(r => r.data);

// =====================================================================
// Samples (2026-09-30) — replaces Client Samples; charged to cost centers
// =====================================================================

export type SampleCategory = 'fabric' | 'embroidery' | 'product_design';
export type SampleAction =
  | 'edit' | 'upload_mood' | 'remove_image' | 'submit' | 'withdraw'
  | 'create_mr' | 'link' | 'start_purchasing' | 'mark_received' | 'upload_purchased'
  | 'designer_approve' | 'designer_reject' | 'ask_presentation' | 'set_appointment' | 'client_decision'
  | 'accept_conditions' | 'deny_conditions' | 'assign_part' | 'assign_code' | 'new_version';

export interface SampleCcNode { id: number; code: string; name: string; typeCode?: string }
export interface SampleImage { key: string; name: string; mime: string; size: number; at?: string; url: string | null }

export interface SampleRow {
  id: number;
  sampleCode: string;
  versionNo: number;
  category: SampleCategory;
  sampleType: 'client' | 'technical';
  size: 'small' | 'presentation';
  title: string;
  description: string;
  specifications: string | null;
  quantityNote: string | null;
  quantityValue: number | null;
  unit: { id: number; name: string } | null;
  unitId: number | null;
  requestNotes: string | null;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  status: string;
  statusLabel: string;
  waitingOn: string;
  daysInStatus: number;
  departmentId: number;
  department: { id: number; code: string; name: string };
  costCenter: SampleCcNode & { isActive: boolean; externalRef: string | null };
  path: SampleCcNode[];
  client: SampleCcNode | null;
  deal: (SampleCcNode & { externalRef: string | null }) | null;
  mrHeader: { id: number; mrId: string; status: string } | null;
  poHeader: { id: number; poId: string; status: string } | null;
  catalogItem: { id: number; partNumber: string; description: string } | null;
  sampleRefCode: string | null;
  vendorName: string | null;
  actualCost: number | null;
  estimatedArrival: string | null;
  procurementNotes: string | null;
  appointmentDate: string | null;
  clientDecision: 'approved' | 'conditional' | 'rejected' | null;
  clientFeedback: string | null;
  imageCount: number;
  versionCount: number;
  createdBy: { id: number; displayName: string };
  createdAt: string;
  updatedAt: string;
}

export interface SampleDetail extends SampleRow {
  parent: { sampleCode: string; versionNo: number; status: string } | null;
  versions: Array<{ sampleCode: string; versionNo: number; status: string }>;
  designerApprovedBy: { id: number; displayName: string } | null;
  designerApprovedAt: string | null;
  clientDecisionBy: { id: number; displayName: string } | null;
  clientDecisionAt: string | null;
  receivedAt: string | null;
  history: Array<{ at: string; by: { id: number; displayName: string }; action: string; note: string | null; from: string | null; to: string; fromLabel: string | null; toLabel: string }>;
  moodImages: SampleImage[];
  purchasedImages: SampleImage[];
  actions: SampleAction[];
  createdMr?: { id: number; mrId: string };
}

export type SampleView = 'waiting' | 'in_progress' | 'decision_due' | 'done' | 'withdrawn' | 'all';

export interface SampleListParams {
  view?: SampleView; q?: string; status?: string; category?: string; sampleType?: string; size?: string;
  departmentId?: number; priority?: string; costCenterId?: number; from?: string; to?: string; page?: number; limit?: number;
}

export interface SampleInput {
  category: SampleCategory; sampleType: 'client' | 'technical'; size: 'small' | 'presentation';
  costCenterId: number; departmentId?: number; title: string; description: string; specifications?: string;
  quantityNote?: string; quantityValue?: number | null; unitId?: number | null; requestNotes?: string; priority: string;
}

export const listSamples = async (params: SampleListParams): Promise<{ rows: SampleRow[]; total: number; page: number; limit: number; viewCounts: Record<SampleView, number> }> =>
  (await api.get('/samples', { params })).data;

export const getSamplesMe = async (): Promise<{ access: string; canCreate: boolean; isProcurement: boolean; seesAll: boolean; departmentIds: number[] }> =>
  (await api.get('/samples/me')).data;

export const getSample = async (code: string): Promise<SampleDetail> => (await api.get(`/samples/${code}`)).data;

export const createSample = async (payload: SampleInput & { send?: boolean }): Promise<SampleDetail> =>
  (await api.post('/samples', payload)).data;

export const updateSample = async (code: string, payload: SampleInput): Promise<SampleDetail> =>
  (await api.put(`/samples/${code}`, payload)).data;

export const uploadSampleImages = async (code: string, kind: 'mood' | 'purchased', files: File[]): Promise<SampleDetail> => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return (await api.post(`/samples/${code}/images/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};

export const sampleAction = async (code: string, action: SampleAction, body: Record<string, any> = {}): Promise<SampleDetail> =>
  (await api.post(`/samples/${code}/actions/${action}`, body)).data;

// ═══════════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const getNotifications = (params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
  api.get('/notifications', { params }).then((r) => r.data);

export const getUnreadNotificationCount = () =>
  api.get('/notifications/unread-count').then((r) => r.data.count as number);

export const markNotificationRead = (id: number) =>
  api.patch(`/notifications/${id}/read`).then((r) => r.data);

export const markAllNotificationsRead = () =>
  api.patch('/notifications/read-all').then((r) => r.data);

export const subscribePush = (subscription: PushSubscriptionJSON) =>
  api.post('/notifications/push/subscribe', subscription).then((r) => r.data);

export const unsubscribePush = (endpoint: string) =>
  api.delete('/notifications/push/unsubscribe', { data: { endpoint } }).then((r) => r.data);

export default api;

// ═══════════════════════════════════════════════════════════════════════════════
// Cost allocation (cost centers on MR / PO lines) — 2026-09-05
// ═══════════════════════════════════════════════════════════════════════════════

export interface CostCenterRef {
  id: number;
  code: string;
  name: string;
  typeCode?: string;
  isActive?: boolean;
  parent?: CostCenterRef | null; // ancestors chain (server sends up to 4 levels up)
}

export interface CostCenterRow extends CostCenterRef {
  typeCode: string;
  label: string;
  externalRef?: string | null;
  notes?: string | null;
  sortOrder: number;
  source: string;
  departmentId?: number | null;
  childCount: number;
  usage: number;
  depth?: number;
  type: { code: string; label: string; parentTypeCode: string | null; childRequired: boolean; anyParent?: boolean; sortOrder: number };
  createdAt?: string;
  updatedAt?: string;
}

export interface CostCenterType {
  code: string;
  label: string;
  parentTypeCode: string | null;
  childRequired: boolean;
  anyParent?: boolean;
  isActive: boolean;
  sortOrder: number;
}

/** Root → node chain */
export const costCenterChain = (cc?: CostCenterRef | null): CostCenterRef[] => {
  const out: CostCenterRef[] = [];
  let cur: CostCenterRef | null | undefined = cc;
  while (cur) { out.unshift(cur); cur = cur.parent; }
  return out;
};

export const costCenterLabel = (cc?: CostCenterRef | null): string => costCenterChain(cc).map((n) => n.name).join(' › ');

export const listAllocCostCenters = async (all = false): Promise<CostCenterRow[]> => {
  const { data } = await api.get('/cost-allocation', { params: all ? { all: 1 } : undefined });
  return data;
};

export const listCostCenterTypes = async (): Promise<CostCenterType[]> => {
  const { data } = await api.get('/cost-allocation/types');
  return data;
};

export const getCostCenterConfig = async (): Promise<{ requiredFrom: string | null; requiredNow: boolean }> => {
  const { data } = await api.get('/cost-allocation/config');
  return data;
};

export const setCostCenterConfig = async (requiredFrom: string | null) => {
  const { data } = await api.put('/cost-allocation/config', { requiredFrom });
  return data;
};

export const createAllocCostCenter = async (payload: {
  code: string; name: string; typeCode: string; parentId?: number | null; externalRef?: string | null; notes?: string | null; sortOrder?: number; departmentId?: number | null;
}) => {
  const { data } = await api.post('/cost-allocation', payload);
  return data;
};

export const updateAllocCostCenter = async (id: number, payload: {
  code?: string; name?: string; externalRef?: string | null; notes?: string | null; sortOrder?: number; isActive?: boolean; departmentId?: number | null; cascade?: boolean;
  ifCodeExists?: 'rename_old' | 'take_over';
}) => {
  const { data } = await api.put(`/cost-allocation/${id}`, payload);
  return data;
};

export const upsertCostCenterType = async (payload: Partial<CostCenterType> & { code: string; label: string }) => {
  const { data } = await api.post('/cost-allocation/types', payload);
  return data;
};

export interface CostCenterImportResult {
  dryRun: boolean;
  source: 'id_register' | 'import';
  added: Array<{ code: string; label: string }>;
  updated: Array<{ code: string; label: string; changes: string[] }>;
  unchanged: number;
  errors: Array<{ row: string; message: string }>;
  logId?: number;
}

export const importCostCenters = async (kind: 'id-register' | 'template' | 'status', file: File, commit: boolean): Promise<CostCenterImportResult> => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post(`/cost-allocation/import/${kind}`, fd, {
    params: commit ? { commit: 1 } : undefined,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const downloadCostCenterStatusSheet = async () => {
  const response = await api.get('/cost-allocation/status.xlsx', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `cost-center-status-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const listCostCenterImports = async () => {
  const { data } = await api.get('/cost-allocation/imports');
  return data;
};

export const setLineCostCenter = async (mrLineId: number, costCenterId: number | null) => {
  const { data } = await api.patch(`/cost-allocation/lines/${mrLineId}`, { costCenterId });
  return data;
};

export const bulkSetLineCostCenter = async (mrLineIds: number[], costCenterId: number | null): Promise<Array<{ mrLineId: number; ok: boolean; error?: string }>> => {
  const { data } = await api.post('/cost-allocation/lines/bulk', { mrLineIds, costCenterId });
  return data;
};

export const listUnallocatedLines = async (params?: { departmentIds?: number[]; status?: string; search?: string; fields?: string[]; limit?: number; offset?: number }) => {
  const { data } = await api.get('/cost-allocation/lines/unallocated', {
    params: {
      ...params,
      departmentIds: params?.departmentIds?.length ? params.departmentIds.join(',') : undefined,
      fields: params?.fields?.length ? params.fields.join(',') : undefined,
    },
  });
  return data as { total: number; rows: any[] };
};

export interface GlobalSearchHit { type: string; id: number | string; title: string; subtitle?: string; url?: string; meta?: Record<string, any> }
export interface GlobalSearchGroup { type: string; label: string; items: GlobalSearchHit[] }
export const globalSearch = async (q: string, types?: string[]): Promise<{ groups: GlobalSearchGroup[]; tookMs: number }> => {
  const { data } = await api.get('/search', { params: { q, types: types?.length ? types.join(',') : undefined } });
  return data;
};

export const listSearchFields = async (): Promise<Array<{ key: string; label: string }>> => {
  const { data } = await api.get('/cost-allocation/lines/search-fields');
  return data;
};

export interface CostAllocationReportParams {
  from?: string; to?: string; costCenterId?: number; departmentId?: number; departmentIds?: number[]; unallocatedOnly?: boolean; includeUnordered?: boolean; stages?: string[];
}

const reportParams = (p?: CostAllocationReportParams) => ({
  from: p?.from || undefined,
  to: p?.to || undefined,
  costCenterId: p?.costCenterId || undefined,
  departmentId: p?.departmentId || undefined,
  departmentIds: p?.departmentIds?.length ? p.departmentIds.join(',') : undefined,
  unallocatedOnly: p?.unallocatedOnly ? 1 : undefined,
  includeUnordered: p?.includeUnordered ? 1 : undefined,
  stages: p?.stages?.length ? p.stages.join(',') : undefined,
});

export const getCostAllocationReport = async (params?: CostAllocationReportParams) => {
  const { data } = await api.get('/cost-allocation/report', { params: reportParams(params) });
  return data as { rows: any[]; summary: any[]; byCategory: any[]; totals: { lines: number; cost: number; unallocated: number } };
};

export const downloadCostAllocationReport = async (params?: CostAllocationReportParams) => {
  const response = await api.get('/cost-allocation/report.xlsx', { params: reportParams(params), responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `cost-allocation-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
